import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "./Reveal";

const PHOTOS = [
  {
    src: "/gallery/drum-circle.jpg",
    alt: "Corporate team playing djembe drums in an SK Media drum circle",
    caption: "Corporate Drum Circle",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    src: "/gallery/stage-main.jpg",
    alt: "Large outdoor concert stage with purple lighting rigs built by SK Media",
    caption: "Arena Stage Production",
    span: "lg:col-span-2",
  },
  {
    src: "/gallery/team-outdoor.jpg",
    alt: "Team celebrating during an outdoor SK Media team building session",
    caption: "Outbound Team Energy",
    span: "lg:col-span-2",
  },
  {
    src: "/gallery/stage-truss.jpg",
    alt: "Night stage truss structure with LED screen and beam lights",
    caption: "Truss & Light Engineering",
    span: "",
  },
  {
    src: "/gallery/team-challenge.jpg",
    alt: "Team circled around a wooden block tower challenge",
    caption: "The Trust Build",
    span: "",
  },
  {
    src: "/gallery/stage-yorun.jpg",
    alt: "Orange branded festival stage with LED wall at YO RUN event",
    caption: "Festival Stage Design",
    span: "lg:col-span-2",
  },
  {
    src: "/gallery/drum-corporate.jpg",
    alt: "Corporate delegates in suits playing djembes at a conference drum circle",
    caption: "Conference Drum Circle",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    src: "/gallery/drum-haldi.jpg",
    alt: "Wedding guests dressed in yellow drumming at a haldi ceremony",
    caption: "Haldi Beats",
    span: "lg:col-span-2",
  },
  {
    src: "/gallery/drum-group.jpg",
    alt: "Team raising colourful djembe drums overhead after a drum circle session",
    caption: "Rhythm Finale",
    span: "lg:col-span-2",
  },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-28 sm:py-36 px-6 lg:px-10"
    >
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <span
              data-testid="gallery-eyebrow"
              className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#d4af37] font-semibold mb-4 block"
            >
              Moments in Gold
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              The <span className="gold-text">Gallery</span>
            </h2>
          </div>
          <p className="text-sm text-zinc-500 max-w-xs leading-relaxed">
            Real frames from SK Media events — drum circles, team energy and
            stages built from the ground up.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[240px] gap-5">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={i % 4} className={p.span}>
              <button
                data-testid={`gallery-item-${i + 1}`}
                onClick={() => setActive(p)}
                className="spotlight-frame group relative w-full h-full overflow-hidden text-left"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-[#09090b]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-4 left-4 font-cinzel text-xs tracking-[0.25em] uppercase text-[#f3e5ab] opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-500">
                  {p.caption}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#09090b]/95 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-[#d4af37] hover:rotate-90 transition-transform duration-300"
              aria-label="Close"
            >
              <X size={32} />
            </button>
            <motion.figure
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.src}
                alt={active.alt}
                className="w-full max-h-[75vh] object-contain border border-[rgba(212,175,55,0.35)]"
              />
              <figcaption className="mt-4 text-center font-cinzel tracking-[0.3em] uppercase text-sm text-[#d4af37]">
                {active.caption}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
